// Port of TrailData/Import/ImportedGeoData.swift.

import type {
  DatasetMetadata,
  PointOfInterest,
  ProtectedArea,
  Trail,
  TrailDataset,
  TrailStage,
  Waypoint,
} from '../core/models';

/**
 * Normalised result of importing an external file (GPX, GeoJSON or KML).
 *
 * Every record carries the import's provenance (by default `userImportProvenance`), imported stages are
 * `userImported` with the unknown-region country code `"ZZ"` unless the file states a country, imported protected
 * areas default to `overnightRule: 'unknown'` and confidence `low`. Nothing imported is ever presented as more
 * authoritative than the file itself.
 */
export interface ImportedGeoData {
  /** At most one imported trail (kind `imported`) grouping all imported line stages. */
  trails: Trail[];
  /** One stage per imported line feature / track. */
  stages: TrailStage[];
  /** Named points that are not recognised POIs (GPX `<wpt>`, uncategorised GeoJSON/KML points). */
  waypoints: Waypoint[];
  /** Points (or area features) whose properties map to a `POICategory`. */
  pointsOfInterest: PointOfInterest[];
  /** Polygon features imported as protected areas. */
  protectedAreas: ProtectedArea[];
  /** Diagnostic messages about skipped or reinterpreted input (developer-facing, not localized). */
  warnings: string[];
}

export function makeImportedGeoData(parts: Partial<ImportedGeoData> = {}): ImportedGeoData {
  return { trails: [], stages: [], waypoints: [], pointsOfInterest: [], protectedAreas: [], warnings: [], ...parts };
}

/** True when no record was imported (warnings are not records). */
export function importedGeoDataIsEmpty(data: ImportedGeoData): boolean {
  return (
    data.trails.length === 0 &&
    data.stages.length === 0 &&
    data.waypoints.length === 0 &&
    data.pointsOfInterest.length === 0 &&
    data.protectedAreas.length === 0
  );
}

/**
 * The imported records as a `TrailDataset` (e.g. to merge into a `TrailDataStore` or to persist). Waypoints have no
 * place in a `TrailDataset` and are not included. Swift `makeDataset(metadata:)`.
 */
export function makeDataset(data: ImportedGeoData, metadata: DatasetMetadata): TrailDataset {
  return {
    metadata,
    trails: data.trails,
    stages: data.stages,
    pointsOfInterest: data.pointsOfInterest,
    protectedAreas: data.protectedAreas,
    regulations: [],
    regulatoryCoverage: [],
  };
}
